<template>
    <component
        :is="to || pathLocale ? NuxtLink : 'button'"
        class="relative inline-flex justify-center items-center border border-transparent font-medium cursor-pointer disabled:pointer-events-none select-none"
        :class="[getCssClass, { 'w-full': full }, { '[&>*]:invisible !text-transparent': loading }]"
        v-bind="{
            ...getAttrsElement,
            [to || pathLocale ? 'to' : 'type']: target(localePath),
        }"
    >
        <div v-if="$slots.left && props.modifier !== BUTTON_MODIFIER.circular" class="relative mr-1 -ml-2">
            <slot name="left" />
        </div>
        <slot />
        <div v-if="$slots.right && props.modifier !== BUTTON_MODIFIER.circular" class="relative ml-1 -mr-2">
            <slot name="right" />
        </div>
        <span v-if="loading" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 !visible">
            <ShcUiLoaderIndicator :color="loadingColor" />
        </span>
    </component>
</template>

<script setup lang="ts">
    import type { Props } from './props'
    import { BUTTON_MODIFIER, BUTTON_SIZE, BUTTON_THEME, BUTTON_TYPE } from '@/components/Shc/Ui/Button/button'

    type LocalePathFn = (path: string) => string

    const props = withDefaults(defineProps<Props>(), {
        isActive: true,
        modifier: undefined,
        size: BUTTON_SIZE.normal,
        theme: BUTTON_THEME.primary,
        to: undefined,
        type: BUTTON_TYPE.button,
        full: false,
        loading: false,
    })

    const NuxtLink = resolveComponent('NuxtLink')

    const attrs = useAttrs()

    const getAttrsElement = computed(() => {
        const returnAttrs = { ...attrs }

        if (!props.isActive || props.loading) {
            returnAttrs['aria-disabled'] = 'true'
            returnAttrs.disabled = 'true'
        }

        if (props.to) {
            returnAttrs.to = props.to
        } else {
            returnAttrs.type = props.type
        }

        return returnAttrs
    })

    function target(localePath: LocalePathFn) {
        let target = ''
        if (props.to || props.pathLocale) {
            target = props.pathLocale ? localePath(props.pathLocale) : (props.to as string)
        } else {
            target = props.type
        }

        return target
    }

    const getCssClass = computed((): string => {
        let cssClass = ''

        switch (props.size) {
            case BUTTON_SIZE.none:
                break
            case BUTTON_SIZE.small:
                cssClass += ' h-8 p-3 text-sm leading-3'
                break
            case BUTTON_SIZE.medium:
                cssClass += ' p-4 text-base'
                break
            case BUTTON_SIZE.big:
                cssClass += ' h-16 p-6 text-xl leading-4'
                break
            default:
                cssClass += ' h-11 text-base'
        }

        switch (props.theme) {
            case BUTTON_THEME.secondary:
                cssClass += ' text-black bg-white'
                break
            case BUTTON_THEME.transparent:
                cssClass += ' border-none bg-transparent text-black'
                break
            case BUTTON_THEME.white:
                cssClass += ' bg-white text-black !border-black'
                break
            default:
                cssClass += ' text-white bg-black'
        }

        switch (props.modifier) {
            case BUTTON_MODIFIER.round:
                cssClass += ' rounded-full'
                break
            case BUTTON_MODIFIER.circular:
                cssClass += ' justify-center w-12 h-12 rounded-full text-white leading-6'
                break
            case BUTTON_MODIFIER.clean:
                cssClass += ' shadow-none text-dark hover:text-dark hover:bg-transparent focus:ring-transparent'
        }

        return cssClass
    })
</script>

<script lang="ts">
    export default { inheritAttrs: false }
</script>

<style lang="scss" scoped>
    .button {
        $self: &;

        &--small {
            #{$self}__slot-left {
                @apply mr-0.5 -ml-1.5;
            }

            #{$self}__slot-right {
                @apply ml-0.5 -mr-1.5;
            }
        }

        &--normal {
            #{$self}__slot-left {
                @apply mr-1 -ml-2;
            }

            #{$self}__slot-right {
                @apply ml-1 -mr-2;
            }
        }

        &--medium {
            #{$self}__slot-left {
                @apply mr-1 -ml-2;
            }

            #{$self}__slot-right {
                @apply ml-1 -mr-2;
            }
        }
    }
</style>
